import { ReceiptPaymentMethod, ReceiptType } from '~core/models/receipt.models';
/* eslint-disable @typescript-eslint/no-unnecessary-condition */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/naming-convention */

import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  input,
  OnChanges,
  output,
  signal,
  SimpleChanges,
  viewChildren
} from '@angular/core';
import { DateRange, MatDateRangePicker } from '@angular/material/datepicker';
import { MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { applyFilterFunction } from '@veone/ngx-utilities/form';
import dayjs from 'dayjs';
import {
  delay,
  distinctUntilChanged,
  EMPTY,
  map,
  of,
  shareReplay,
  switchMap
} from 'rxjs';
import { EventService } from '~backoffice/app/~shared/services/event.service';
import { OrganizationService } from '~backoffice/app/~shared/services/organization.service';
import { UserService } from '~backoffice/app/~shared/services/user.service';
import {
  ListFilter,
  ResourceDisposition,
  SelectData
} from '~core/models/shared.models';

import { roundingRules } from '~backoffice/app/~shared/constants/global.constants';
import { CustomerSupplierService } from '~backoffice/app/~shared/services/customer-supplier.service';
import { AuditServiceName } from '~core/models/auditor.models';
import {
  TaxBase,
  TaxKind,
  TaxScope,
  UnitMeasureType
} from '~core/models/core.models';
import {
  CustomInvoiceStatus,
  InvoiceLifecycleState,
  InvoiceLifecycleStateLabels,
  InvoiceType
} from '~core/models/invoice.models';
import { OrganizationStatusLabel } from '~core/models/organization.models';
import {
  PaymentMethod,
  PaymentOperator,
  TransactionResultStatus
} from '~core/models/payment.models';
import { StickerMovementType } from '~core/models/sticker.models';
import { ConstantsService } from '~core/services/constants.service';
import {
  getInvoiceStatusName,
  getInvoiceTypeName
} from '~core/utils/invoice.utils';
import { getOrganizationStatusLabelName } from '~core/utils/organization';
import { getReceiptTypeName } from '~core/utils/receipt.utils';
import {
  getTaxBaseName,
  getTaxKindName,
  getTaxScopeName
} from '~core/utils/tax.utils';
import { getTransactionStatusName } from '~core/utils/transaction-status.utils';
import { EstablishmentService } from '~core/services/establishment.service';
import { SalesPointService } from '~core/services/sales-point.service';
import { isEmpty } from 'lodash-es';
import { match } from 'ts-pattern';
import { UnitMeasureTypePipe } from '~core/pipes/unit-measure-type.pipe';

type FilterName =
  | 'products'
  | 'auditLogs'
  | 'salePoints'
  | 'stickers'
  | 'organization'
  | 'users'
  | 'taxList'
  | 'currencies'
  | 'invoice'
  | 'billingEquipments'
  | 'receiptList'
  | 'unitMeasure'
  | 'payments'
  | 'dashboardList'
  | 'taxRules'
  | 'taxProfileList'
  | 'taxProfileOrganizationList';

enum StatusCode {
  ok = 200,
  created = 201,
  noContent = 204,
  badRequest = 400,
  unauthorized = 401,
  forbidden = 403,
  notFound = 404,
  internalServerError = 500,
  serviceUnavailable = 503
}

enum HttpMethods {
  get = 'GET',
  post = 'POST',
  put = 'PUT',
  patch = 'PATCH',
  delete = 'DELETE'
}

@Component({
  selector: 'search-bar',
  standalone: false,
  templateUrl: './search-bar.component.html',
  styleUrls: ['./search-bar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SearchBarComponent implements OnChanges {
  elementRef = inject(ElementRef);
  eventService = inject(EventService);
  userService = inject(UserService);
  organizationService = inject(OrganizationService);
  customerSupplierService = inject(CustomerSupplierService);
  private _establishmentService = inject(EstablishmentService);
  private _salesPointService = inject(SalesPointService);
  private _constantsService = inject(ConstantsService);
  private _unitMeasureTypePipe = new UnitMeasureTypePipe();

  private _currencyCodes$ = this._constantsService.getCurrencyList();

  private _organizationFiltersValues$ = this.organizationService
    .getOrganizationFiltersValues()
    .pipe(
      map(res => {
        return {
          taxSystems: res.taxSystems.map(item => ({
            name: item,
            value: item
          })),
          activitySectors: res.activitySectors.map(item => ({
            name: item,
            value: item
          })),
          taxDirections: res.taxDirections.map(item => ({
            name: item,
            value: item
          })),
          accountRoleLabels: res.accountRoleLabels.map(item => ({
            name: item,
            value: item
          }))
        };
      }),
      shareReplay(1)
    );

  roundingRules = roundingRules;

  inputList = viewChildren<MatInput>(MatInput);
  selectList = viewChildren<MatSelect>(MatSelect);
  rangeList = viewChildren<any>(MatDateRangePicker);

  add = output<void>();
  layoutChange = output<void>();
  search = output<string | null>();
  filterChange = output<Record<string, any>>();

  count = input<number | null | undefined>(null);
  countTitle = input<string | null | undefined>(null);
  title = input<string | null | undefined>(null);
  addButtonTooltip = input<string>('');
  allowAddButton = input<boolean>(true);
  addButtonLabel = input<string>('Ajouter');
  addIcon = input<string | null | undefined>(null);
  allowRefreshButton = input<boolean>(true);
  allowSearchInput = input<boolean>(true);
  searchPlaceholder = input<string | null | undefined>('');
  defaultSearchText = input<string>('');
  layoutType = input<ResourceDisposition | null | undefined>(null);
  filterName = input<FilterName | undefined>(undefined);

  listFilters: ListFilter[] | undefined;
  searchAutoCompleteFn:
    | Pick<ListFilter, 'autocompleteValues$'>['autocompleteValues$']
    | undefined;

  isListFilterVisible = signal(false);

  searchForm = signal({ search: this.defaultSearchText() ?? '' });

  private _selectedEstablishement = signal<string | undefined>(undefined);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['filterName'] && changes['filterName'].currentValue) {
      this.generateListFilter();
    }
  }

  searchClicked(event: string | null): void {
    this.search.emit(event);
  }

  addClicked(): void {
    this.add.emit();
  }

  emitRefresh(): void {
    this.eventService.emit({ type: 'REFRESH_DATA' });
  }

  private _updateSalePointFilter(establishmentId: string | undefined): void {
    const salePointFilter = this.listFilters?.find(
      f => f.requestKey === 'salesPointId'
    );
    if (!salePointFilter) return;
    salePointFilter.requestValue = null;

    const organizationId = this.listFilters?.find(
      f => f.requestKey === 'organizationId'
    )?.requestValue;

    // if (establishmentId) {
    //   salePointFilter.selectValues$ = this._salesPointService
    //     .list({
    //       establishmentId: establishmentId,
    //       itemPerPage: 100_000_000,
    //       enabled: true
    //     })
    //     .pipe(
    //       map(response =>
    //         response.items.map(item => ({
    //           name: item.name,
    //           value: item.id
    //         }))
    //       )
    //     );
    // } else {
    //   salePointFilter.selectValues$ = this._salesPointService
    //     .list({
    //       ...(organizationId ? { organizationId } : {}),
    //       itemPerPage: 100_000_000,
    //       enabled: true
    //     })
    //     .pipe(
    //       map(response =>
    //         response.items.map(item => ({
    //           name: item.establishment ? `${item.name}` : item.name,
    //           value: item.id
    //         }))
    //       )
    //     );
    // }
  }

  generateListFilter(): void {
    const name = this.filterName();
    if (!name) {
      throw Error('Aucun filterName défini');
    }

    const enabledDisabledOptions = of([
      { name: 'Actif', value: true },
      { name: 'Inactif', value: false }
    ]);

    const filterMaps: Record<FilterName, ListFilter[]> = {
      auditLogs: [
        {
          requestKey: 'organizationId',
          requestValue: null,
          displayedName: 'Organisation',
          displayedPlaceholder: 'Rechercher',
          inputType: 'autocomplete',
          autocompleteValues$: (name?: string | null) => {
            const inputElement = this.elementRef.nativeElement.querySelector(
              '#search-bar-autocomplete-' + 'organizationId'
            ) as HTMLInputElement | null;
            if (!inputElement) return EMPTY;
            const nameQuery = name ? name.trim() : null;
            if (!nameQuery) return EMPTY;
            return of(inputElement.value).pipe(
              distinctUntilChanged(),
              delay(600),
              switchMap(() =>
                this.organizationService.list(
                  nameQuery ? { name: `like(%${nameQuery}%)` } : {}
                )
              ),
              map(res =>
                res.items.map(org => ({ name: org.name, value: org.id }))
              )
            );
          }
        },
        {
          requestKey: 'serviceName',
          requestValue: null,
          displayedName: 'Service',
          inputType: 'select',
          selectValues$: of(Object.entries(AuditServiceName)).pipe(
            map(res =>
              res.map(([_, value]) => ({
                name: value,
                value
              }))
            )
          )
        },
        {
          requestKey: 'statusCode',
          requestValue: null,
          displayedName: 'Statut',
          inputType: 'select',
          selectValues$: of([
            { name: 'OK', value: StatusCode.ok },
            { name: 'Créé', value: StatusCode.created },
            { name: 'Aucun contenu', value: StatusCode.noContent },
            { name: 'Mauvaise Requête', value: StatusCode.badRequest },
            { name: 'Non Autorisé', value: StatusCode.unauthorized },
            { name: 'Interdit', value: StatusCode.forbidden },
            { name: 'Introuvable', value: StatusCode.notFound },
            {
              name: 'Erreur interne du serveur',
              value: StatusCode.internalServerError
            },
            {
              name: 'Service Indisponibile',
              value: StatusCode.serviceUnavailable
            }
          ])
        },
        {
          requestKey: 'endpointMethod',
          requestValue: null,
          displayedName: 'Methode HTTP',
          inputType: 'select',
          selectValues$: of([
            { name: 'GET', value: HttpMethods.get },
            { name: 'POST', value: HttpMethods.post },
            { name: 'PUT', value: HttpMethods.put },
            { name: 'PATCH', value: HttpMethods.patch },
            { name: 'DELETE', value: HttpMethods.delete }
          ])
        },
        {
          requestKey: 'accountId',
          requestValue: null,
          displayedName: 'Utilisateur',
          displayedPlaceholder: 'Rechercher un e-mail',
          inputType: 'autocomplete',
          autocompleteValues$: (name?: string | null) => {
            const inputElement = this.elementRef.nativeElement.querySelector(
              '#search-bar-autocomplete-' + 'accountId'
            ) as HTMLInputElement | null;
            if (!inputElement) return EMPTY;
            const nameQuery = name ? name.trim() : null;
            if (!nameQuery) return EMPTY;
            return of(inputElement.value).pipe(
              distinctUntilChanged(),
              delay(600),
              switchMap(() =>
                this.userService.listUser(
                  nameQuery ? { email: `like(%${nameQuery}%)` } : {}
                )
              ),
              map(users =>
                users.items.map(user => ({
                  name: user.firstName + ' ' + user.lastName,
                  value: user.id
                }))
              )
            );
          }
        },
        {
          requestKey: 'ipAddress',
          requestValue: null,
          requestFunction: 'like',
          displayedName: 'Adresse IP'
        }
      ],
      stickers: [
        {
          requestKey: 'organizationId',
          requestValue: null,
          displayedName: 'Raison sociale / NCC',
          displayedPlaceholder: 'Rechercher',
          inputType: 'autocomplete',
          autocompleteValues$: (name?: string | null) => {
            const inputElement = this.elementRef.nativeElement.querySelector(
              '#search-bar-autocomplete-' + 'organizationId'
            ) as HTMLInputElement | null;
            if (!inputElement) return EMPTY;
            const nameQuery = name ? name.trim() : null;
            if (!nameQuery) return EMPTY;
            return of(inputElement.value).pipe(
              distinctUntilChanged(),
              delay(600),
              switchMap(nameQuery => {
                const trimmed = nameQuery?.trim();
                if (!trimmed) return EMPTY;
                const isNif = /^\d{7}[A-Za-z]$/.test(trimmed);
                return this.organizationService
                  .list({ orNif: trimmed, orName: trimmed })
                  .pipe(
                    map(res =>
                      res.items.map(org => ({
                        name: isNif ? org.nif : org.name,
                        value: org.id
                      }))
                    )
                  );
              })
            );
          }
        },
        {
          requestKey: 'status',
          requestValue: null,
          displayedName: 'Statut',
          inputType: 'select',
          selectValues$: of([
            'pending',
            'failed',
            'completed',
            'refused',
            'processing',
            'reversed',
            'canceled',
            'accepted',
            'unknown',
            'expired'
          ] as TransactionResultStatus[]).pipe(
            map(res =>
              res.map(value => ({
                name: getTransactionStatusName(value),
                value
              }))
            )
          )
        },
        {
          requestKey: 'type',
          requestValue: null,
          displayedName: "Type d'opération",
          inputType: 'select',
          selectValues$: of([
            { name: 'Consommation', value: StickerMovementType.debit },
            { name: 'Rechargement', value: StickerMovementType.credit }
          ])
        }
      ],
      invoice: [
        {
          requestKey: 'organizationId',
          requestValue: null,
          displayedName: 'Organisation',
          displayedPlaceholder: 'Rechercher',
          inputType: 'autocomplete',
          autocompleteValues$: (name?: string | null) => {
            const inputElement = this.elementRef.nativeElement.querySelector(
              '#search-bar-autocomplete-' + 'organizationId'
            ) as HTMLInputElement | null;
            if (!inputElement) return EMPTY;
            const nameQuery = name ? name.trim() : null;
            if (!nameQuery) return EMPTY;
            return of(inputElement.value).pipe(
              distinctUntilChanged(),
              delay(600),
              switchMap(() =>
                this.organizationService.list(
                  nameQuery ? { name: `like(%${nameQuery}%)` } : {}
                )
              ),
              map(res =>
                res.items.map(org => ({ name: org.name, value: org.id }))
              )
            );
          }
        },
        {
          requestKey: 'customerId',
          requestValue: null,
          displayedName: 'Fournisseur',
          displayedPlaceholder: 'Rechercher',
          inputType: 'autocomplete',
          autocompleteValues$: (name?: string | null) => {
            const inputElement = this.elementRef.nativeElement.querySelector(
              '#search-bar-autocomplete-' + 'customerId'
            ) as HTMLInputElement | null;
            if (!inputElement) return EMPTY;
            const nameQuery = name ? name.trim() : null;
            if (!nameQuery) return EMPTY;
            return of(inputElement.value).pipe(
              distinctUntilChanged(),
              delay(600),
              switchMap(() =>
                this.customerSupplierService.list(
                  nameQuery ? { name: `like(%${nameQuery}%)` } : {}
                )
              ),
              map(res =>
                res.items.map(org => ({ name: org.name, value: org.id }))
              )
            );
          }
        },
        {
          requestKey: 'establishmentId',
          requestValue: null,
          displayedName: 'Établissement émetteur',
          displayedPlaceholder: 'Rechercher',
          inputType: 'autocomplete',
          autocompleteValues$: (name?: string | null) => {
            const inputElement = this.elementRef.nativeElement.querySelector(
              '#search-bar-autocomplete-' + 'establishmentId'
            ) as HTMLInputElement | null;
            if (!inputElement) return EMPTY;
            const nameQuery = name ? name.trim() : null;
            if (!nameQuery) return EMPTY;
            return of(inputElement.value).pipe(
              distinctUntilChanged(),
              delay(600),
              switchMap(() =>
                this._establishmentService.list(
                  nameQuery ? { name: `like(%${nameQuery}%)` } : {}
                )
              ),
              map(res =>
                res.items.map(org => ({ name: org.name, value: org.id }))
              )
            );
          }
        },
        {
          requestKey: 'salesPointId',
          requestValue: null,
          displayedName: 'Point de vente',
          displayedPlaceholder: 'Rechercher',
          inputType: 'autocomplete',
          autocompleteValues$: (name?: string | null) => {
            const inputElement = this.elementRef.nativeElement.querySelector(
              '#search-bar-autocomplete-' + 'salesPointId'
            ) as HTMLInputElement | null;
            if (!inputElement) return EMPTY;
            const nameQuery = name ? name.trim() : null;
            if (!nameQuery) return EMPTY;
            return of(inputElement.value).pipe(
              distinctUntilChanged(),
              delay(600),
              switchMap(() =>
                this._salesPointService.list(
                  nameQuery ? { name: `like(%${nameQuery}%)` } : {}
                )
              ),
              map(res =>
                res.items.map(org => ({ name: org.name, value: org.id }))
              )
            );
          }
        },
        {
          requestKey: 'customStatus',
          requestValue: null,
          displayedName: 'Statut',
          inputType: 'multi-select',
          selectValues$: of(
            Object.entries(CustomInvoiceStatus).filter(
              ([, status]) => status !== CustomInvoiceStatus.proforma
            )
          ).pipe(
            map(res =>
              res.map(([_, value]) => ({
                name: getInvoiceStatusName(value),
                value
              }))
            )
          )
        },
        {
          requestKey: 'type',
          requestValue: null,
          displayedName: 'Type de facture',
          inputType: 'multi-select',
          requestFunction: 'in',
          selectValues$: of(Object.entries(InvoiceType)).pipe(
            map(res =>
              res.map(([_, value]) => ({
                name: getInvoiceTypeName(value),
                value
              }))
            )
          )
        },
        {
          requestKey: 'certificatedAt',
          requestValue: null,
          displayedName: 'Date de certification',
          inputType: 'date-range',
          requestFunction: 'range'
        },
        {
          requestKey: 'lifecycleState',
          requestValue: null,
          displayedName: 'Etat',
          inputType: 'multi-select',
          requestFunction: 'in',
          selectValues$: of(
            Object.values(InvoiceLifecycleState).map(value => ({
              value,
              name: InvoiceLifecycleStateLabels[value]
            }))
          )
        }
      ],
      products: [],
      salePoints: [],
      organization: [
        {
          requestKey: 'idu',
          requestValue: null,
          requestFunction: 'like',
          displayedName: 'IDU'
        },
        {
          requestKey: 'nif',
          requestValue: null,
          requestFunction: 'like',
          displayedName: 'NCC'
        },
        {
          requestKey: 'activitySector',
          requestValue: null,
          requestFunction: 'like',
          inputType: 'select',
          displayedName: "Secteur d'activité",
          selectValues$: this._organizationFiltersValues$.pipe(
            map(res => res.activitySectors)
          )
        },
        {
          requestKey: 'taxDirection',
          requestValue: null,
          requestFunction: 'like',
          inputType: 'select',
          displayedName: 'Direction régionale',
          selectValues$: this._organizationFiltersValues$.pipe(
            map(res => res.taxDirections)
          )
        },
        {
          requestKey: 'taxSystem',
          requestValue: null,
          requestFunction: 'like',
          inputType: 'select',
          displayedName: "Régime d'imposition",
          selectValues$: this._organizationFiltersValues$.pipe(
            map(res => res.taxSystems)
          )
        },
        {
          requestKey: 'enabled',
          requestValue: null,
          displayedName: 'Statut',
          inputType: 'select',
          selectValues$: enabledDisabledOptions
        },
        {
          requestKey: 'onboardingCompletedAt',
          requestValue: null,
          displayedName: "Statut d'inscription",
          inputType: 'select',
          selectValues$: of([
            { name: 'Terminée', value: 'isNotNull()' },
            { name: 'Incomplète', value: 'isNull()' }
          ])
        },
        {
          requestKey: 'statusLabel',
          requestValue: null,
          displayedName: 'Statut DGI',
          inputType: 'multi-select',
          requestFunction: 'in',
          selectValues$: of(Object.entries(OrganizationStatusLabel)).pipe(
            map(res =>
              res.map(([_, value]) => ({
                name: getOrganizationStatusLabelName(value),
                value
              }))
            )
          )
        },
        {
          requestKey: 'taxCenter',
          requestValue: null,
          displayedName: 'Poste Comptable',
          inputType: 'string',
          requestFunction: 'like'
        }
      ],
      users: [
        {
          requestKey: 'locked',
          requestValue: null,
          displayedName: 'Statut',
          inputType: 'select',
          selectValues$: of([
            { name: 'Actif', value: false },
            { name: 'Inactif', value: true }
          ])
        }
      ],
      unitMeasure: [
        {
          requestKey: 'type',
          requestValue: null,
          displayedName: "Type d'unités de mésures",
          inputType: 'select',
          selectValues$: of(
            Object.keys(UnitMeasureType).map(key => ({
              value: UnitMeasureType[key as keyof typeof UnitMeasureType],
              name: this._unitMeasureTypePipe.transform(
                UnitMeasureType[key as keyof typeof UnitMeasureType]
              )
            }))
          )
        }
      ],
      taxList: [
        {
          requestKey: 'kind',
          requestValue: null,
          displayedName: 'Type',
          inputType: 'select',
          selectValues$: of(Object.entries(TaxKind)).pipe(
            map(res =>
              res.map(([_, value]) => ({
                name: getTaxKindName(value),
                value
              }))
            )
          )
        },
        {
          requestKey: 'base',
          requestValue: null,
          displayedName: 'Base de calcul',
          inputType: 'select',
          selectValues$: of(Object.entries(TaxBase)).pipe(
            map(res =>
              res.map(([_, value]) => ({
                name: getTaxBaseName(value),
                value
              }))
            )
          )
        },
        {
          requestKey: 'scope',
          requestValue: null,
          displayedName: 'Niveau d’application',
          inputType: 'select',
          selectValues$: of(Object.entries(TaxScope)).pipe(
            map(res =>
              res.map(([_, value]) => ({
                name: getTaxScopeName(value),
                value
              }))
            )
          )
        },
        {
          requestKey: 'enabled',
          requestValue: null,
          displayedName: 'Statut',
          inputType: 'select',
          selectValues$: enabledDisabledOptions
        }
      ],
      currencies: [
        {
          requestKey: 'code',
          requestValue: null,
          displayedName: 'Code',
          inputType: 'select',
          selectValues$: this._currencyCodes$.pipe(
            map(codes =>
              codes.map(code => ({
                value: code,
                name: code
              }))
            )
          )
        },
        {
          requestKey: 'position',
          requestValue: null,
          displayedName: 'Position',
          inputType: 'select',
          selectValues$: of([
            { name: 'Gauche', value: 'left' },
            { name: 'Droite', value: 'right' }
          ])
        },
        {
          requestKey: 'roundingRule',
          requestValue: null,
          displayedName: "Règle d'arrondi",
          inputType: 'select',
          selectValues$: of(roundingRules)
        },
        {
          requestKey: 'enabled',
          requestValue: null,
          displayedName: 'Statut',
          inputType: 'select',
          selectValues$: of([
            { name: 'Activé', value: true },
            { name: 'Désactivé', value: false }
          ])
        }
      ],
      billingEquipments: [],
      payments: [
        {
          requestKey: 'paymentMethod',
          requestValue: null,
          displayedName: 'Méthode de paiement',
          inputType: 'multi-select',
          requestFunction: 'in',
          selectValues$: of([
            { name: 'Orange Money', value: PaymentMethod.ORANGE_MONEY },
            { name: 'Moov Money', value: PaymentMethod.MOOV_MONEY },
            { name: 'MTN Money', value: PaymentMethod.MTN_MONEY },
            { name: 'Espèces', value: ReceiptPaymentMethod.CASH },
            { name: 'Carte bancaire', value: ReceiptPaymentMethod.CARD },
            { name: 'Paiement à terme', value: ReceiptPaymentMethod.DEFERRED }
          ])
        },
        {
          requestKey: 'phoneNumber',
          requestValue: null,
          requestFunction: 'like',
          inputType: 'number',
          displayedName: 'Numéro de téléphone'
        },
        {
          requestKey: 'resultStatus',
          requestValue: null,
          displayedName: 'Statut',
          inputType: 'multi-select',
          requestFunction: 'in',
          selectValues$: of([
            'accepted',
            'canceled',
            'completed',
            'expired',
            'failed',
            'pending',
            'processing',
            'refused',
            'reversed',
            'unknown'
          ] as TransactionResultStatus[]).pipe(
            map(res =>
              res.map(value => ({
                name: getTransactionStatusName(value),
                value
              }))
            )
          )
        },
        {
          requestKey: 'organizationId',
          requestValue: null,
          displayedName: 'Raison sociale / NCC',
          displayedPlaceholder: 'Rechercher',
          inputType: 'autocomplete',
          autocompleteValues$: (name?: string | null) => {
            const inputElement = this.elementRef.nativeElement.querySelector(
              '#search-bar-autocomplete-' + 'organizationId'
            ) as HTMLInputElement | null;
            if (!inputElement) return EMPTY;
            const nameQuery = name ? name.trim() : null;
            if (!nameQuery) return EMPTY;
            return of(inputElement.value).pipe(
              distinctUntilChanged(),
              delay(600),
              switchMap(nameQuery => {
                const trimmed = nameQuery?.trim();
                if (!trimmed) return EMPTY;

                const isNif = /^\d{7}[A-Za-z]$/.test(trimmed);

                return this.organizationService
                  .list({ orNif: trimmed, orName: trimmed })
                  .pipe(
                    map(res =>
                      res.items.map(org => ({
                        name: isNif ? org.nif : org.name,
                        value: org.id
                      }))
                    )
                  );
              })
            );
          }
        }
      ],
      taxProfileList: [
        {
          requestKey: 'enabled',
          requestValue: null,
          displayedName: 'Statut',
          inputType: 'select',
          selectValues$: enabledDisabledOptions
        }
      ],
      taxProfileOrganizationList: [
        {
          requestKey: 'idu',
          requestValue: null,
          requestFunction: 'like',
          displayedName: 'IDU'
        },
        {
          requestKey: 'nif',
          requestValue: null,
          requestFunction: 'like',
          displayedName: 'NCC'
        },
        {
          requestKey: 'activitySector',
          requestValue: null,
          requestFunction: 'like',
          inputType: 'select',
          displayedName: "Secteur d'activité",
          selectValues$: this._organizationFiltersValues$.pipe(
            map(res => res.activitySectors)
          )
        },
        {
          requestKey: 'taxDirection',
          requestValue: null,
          requestFunction: 'like',
          inputType: 'select',
          displayedName: 'Direction régionale',
          selectValues$: this._organizationFiltersValues$.pipe(
            map(res => res.taxDirections)
          )
        },
        {
          requestKey: 'taxSystem',
          requestValue: null,
          requestFunction: 'like',
          inputType: 'select',
          displayedName: "Régime d'imposition",
          selectValues$: this._organizationFiltersValues$.pipe(
            map(res => res.taxSystems)
          )
        }
      ],
      receiptList: [
        {
          requestKey: 'receiptType',
          requestValue: null,
          displayedName: 'Type',
          inputType: 'multi-select',
          requestFunction: 'in',
          selectValues$: of(Object.entries(ReceiptType)).pipe(
            map(res =>
              res.map(([_, value]) => ({
                name: getReceiptTypeName(value),
                value
              }))
            )
          )
        },
        {
          requestKey: 'establishmentCode',
          requestValue: null,
          requestFunction: 'like',
          displayedName: 'Code Établissement'
        },
        {
          requestKey: 'merchantName',
          requestValue: null,
          requestFunction: 'like',
          displayedName: 'Émetteur'
        },
        {
          requestKey: 'clientName',
          requestValue: null,
          requestFunction: 'like',
          displayedName: 'Identité Client'
        },
        {
          requestKey: 'paymentMethod',
          requestValue: null,
          displayedName: 'Méthode de paiement',
          inputType: 'multi-select',
          requestFunction: 'in',
          selectValues$: of([
            { name: 'Mobile Money', value: ReceiptPaymentMethod.MOBILE_MONEY },
            { name: 'Espèces', value: ReceiptPaymentMethod.CASH },
            { name: 'Carte bancaire', value: ReceiptPaymentMethod.CARD },
            { name: 'Paiement à terme', value: ReceiptPaymentMethod.DEFERRED }
          ])
        },
        {
          requestKey: 'paymentOperator',
          requestValue: null,
          inputType: 'multi-select',
          requestFunction: 'in',
          displayedName: 'Opérateur de paiement',
          selectValues$: of([
            { name: 'Orange', value: PaymentOperator.ORANGE },
            { name: 'MTN', value: PaymentOperator.MTN },
            { name: 'Moov', value: PaymentOperator.MOOV }
          ])
        },
        {
          requestKey: 'transactionId',
          requestValue: null,
          requestFunction: 'like',
          displayedName: 'Id de transaction'
        },
        {
          requestKey: 'saleId',
          requestValue: null,
          requestFunction: 'like',
          displayedName: 'Id de vente'
        }
      ],
      taxRules: [],
      dashboardList: [
        // {
        //   requestKey: 'enabled',
        //   requestValue: null,
        //   displayedName: 'Statut',
        //   inputType: 'select',
        //   selectValues$: enabledDisabledOptions
        // }
      ]
    };

    this.listFilters = this.filterName()
      ? filterMaps[this.filterName() as FilterName]
      : [];

    switch (name) {
      // case 'stickers':
      //   this.searchAutoCompleteFn = (name?: string | null) => {
      //     const nameQuery = name ? name.trim() : null;
      //     return this.organizationService
      //       .list(nameQuery ? { name: `like(%${nameQuery}%)` } : {})
      //       .pipe(
      //         map(res =>
      //           res.items.map(org => ({ name: org.name, value: org.id }))
      //         )
      //       );
      //   };
      //   break;

      default:
        break;
    }
  }

  setFilterValue(key: string, value: any): void {
    const filter = this.listFilters?.find(el => el.requestKey === key);
    if (filter) {
      filter.requestValue = typeof value === 'string' ? value.trim() : value;
      if (this.filterName() === 'invoice' && key === 'establishmentId') {
        const establishmentId = value ?? undefined;
        this._selectedEstablishement.set(establishmentId);
        this._updateSalePointFilter(establishmentId);
      }
      if (this.filterName() === 'invoice' && key === 'organizationId') {
        this._updateSalePointFilter(this._selectedEstablishement());
      }
      this.emitFilter();
    }
  }

  clearListFilterValues(): void {
    this.inputList()?.forEach(input => (input.value = null));
    this.selectList()?.forEach(select => select.writeValue(null));
    this.rangeList()?.forEach(el => el.select(null));
    this.listFilters = this.listFilters?.map(el => ({
      ...el,
      requestValue: null
    }));
    if (this.filterName() === 'invoice') {
      this._selectedEstablishement.set(undefined);
    }
    this.emitFilter();
  }

  toggleFilterSheet(): void {
    this.isListFilterVisible.update(current => !current);
  }

  switchLayoutType(): void {
    this.layoutChange.emit();
  }

  emitFilter(): void {
    type FilterValue = string | number | boolean | Date | null;
    const params: Record<string, FilterValue> = {};

    this.listFilters?.forEach(el => {
      const value = el.requestValue;

      const valueToSet = match(value)
        .when(
          value => Array.isArray(value) && isEmpty(value),
          () => null
        )
        .when(
          value => value != null && value !== '',
          () => applyFilterFunction(value, el.requestFunction) as FilterValue
        )
        .otherwise(() => null);

      params[el.requestKey] = valueToSet;
    });

    this.filterChange.emit(params);
  }

  selectDate(key: string, value: any): void {
    this.setFilterValue(key, dayjs(value).toISOString());
  }

  selectDateRange(key: string, value: DateRange<string> | null): void {
    if (!value?.start || !value?.end) {
      this.setFilterValue(key, null);
      return;
    }
    const range = `${dayjs(value.start).startOf('day').toISOString()},${dayjs(value.end).endOf('day').toISOString()}`;
    this.setFilterValue(key, range);
  }

  trackByFilter(_index: number, listFilter: ListFilter): string {
    return listFilter.requestKey;
  }

  trackByOption(_index: number, option: SelectData): string | number {
    return option.value;
  }

  selectOptionDisplayFn(option: SelectData): string {
    return option && option.name ? option.name : '';
  }

  get appliedFilterCount(): number | undefined {
    return this.listFilters?.filter(el => {
      const v = el.requestValue;

      return !(
        v === null ||
        v === undefined ||
        v === '' ||
        (Array.isArray(v) && v.length === 0)
      );
    }).length;
  }

  clearFilter(filter: ListFilter, input: HTMLInputElement): void {
    input.value = '';
    if (filter.requestValue) {
      this.setFilterValue(filter.requestKey, null);
    }
  }

  onInputChange(filter: ListFilter, input: HTMLInputElement): void {
    if (!input.value || !input.value.trim()) {
      this.clearFilter(filter, input);
    }
  }

  getLabelName(value: any, options: SelectData[]): string {
    const option = options.find(opt => opt.value === value);
    return option ? option.name : value;
  }
}
